import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldCheck, RefreshCw, Loader2, CheckCircle2, AlertTriangle, MinusCircle,
    LayoutGrid, Scale, CalendarRange, Trash2, ExternalLink, Wrench,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { fmtInt, fmtNumber } from "@/lib/format";
import { apiErrorText } from "@/lib/errorText";
import { OPS } from "@/constants/testIds";

const DA = OPS.dataAudit;

const STATUS_TONE = {
    complete: { bg: "var(--success-soft)", fg: "var(--success)", Icon: CheckCircle2 },
    pass: { bg: "var(--success-soft)", fg: "var(--success)", Icon: CheckCircle2 },
    partial: { bg: "var(--warning-soft)", fg: "#B45309", Icon: AlertTriangle },
    skipped: { bg: "var(--bg-surface-2)", fg: "var(--ink-muted)", Icon: MinusCircle },
    missing: { bg: "var(--danger-soft)", fg: "var(--danger)", Icon: AlertTriangle },
    fail: { bg: "var(--danger-soft)", fg: "var(--danger)", Icon: AlertTriangle },
};

function Pill({ status, children }) {
    const tone = STATUS_TONE[status] || STATUS_TONE.skipped;
    const Icon = tone.Icon;
    return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ backgroundColor: tone.bg, color: tone.fg }}>
            <Icon className="h-3 w-3" />
            {children}
        </span>
    );
}

function Stat({ k, label, value, tone }) {
    const { lang } = useI18n();
    return (
        <div data-testid={DA.stat(k)} className="rounded-2xl border border-[color:var(--border-soft)] surface px-4 py-3">
            <p className="truncate text-xs text-[color:var(--ink-muted)]">{label}</p>
            <p className="kpi-number text-2xl font-extrabold tnum" style={{ color: tone || "var(--ink)" }}>
                <bdi dir="ltr">{value === null || value === undefined ? "\u2014" : fmtInt(value, lang)}</bdi>
            </p>
        </div>
    );
}

function TabSkeleton({ testId, note }) {
    return (
        <div data-testid={testId} className="space-y-2">
            {note ? (
                <p className="flex items-center gap-2 text-xs text-[color:var(--ink-muted)]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--brand-orange)]" />{note}
                </p>
            ) : null}
            <Skeleton className="h-[220px] w-full rounded-2xl" />
        </div>
    );
}

function monthsBack(n) {
    const out = [];
    const d = new Date();
    for (let i = 0; i < n; i++) {
        out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
        d.setMonth(d.getMonth() - 1);
    }
    return out;
}

export default function DataAuditPage() {
    const { t, lang, dir } = useI18n();
    const months = useMemo(() => monthsBack(30), []);
    const [month, setMonth] = useState(months[1] || months[0]);
    const [tab, setTab] = useState("cards");
    const [cards, setCards] = useState(null);
    const [checks, setChecks] = useState(null);
    const [coverage, setCoverage] = useState(null);
    const [anoms, setAnoms] = useState(null);
    const [loading, setLoading] = useState({});   // per section: cards | checks | coverage | anoms
    const [fixing, setFixing] = useState(null);
    const [preview, setPreview] = useState(null);   // dry-run result awaiting confirmation

    const label = (r) => (lang === "ar" ? r.label_ar || r.label_en : r.label_en || r.label_ar);
    const reasonText = (rs) => (!rs ? "" : lang === "ar" ? rs.ar : rs.en);

    // Each section loads on its own: the page used to wait for all four requests,
    // and the coverage grid alone takes ~16 s on production. Coverage is asked only
    // once its tab is opened. A newer request for a section wins over an older one.
    const seq = useRef({});
    const fetchOne = useCallback(async (key, url, params, set) => {
        const n = (seq.current[key] || 0) + 1;
        seq.current[key] = n;
        setLoading((s) => ({ ...s, [key]: true }));
        try {
            const r = await api.get(url, params ? { params } : undefined);
            if (seq.current[key] === n) set(r.data);
        } catch (e) {
            if (seq.current[key] === n) toast.error(apiErrorText(e, t("da_load_failed")));
        } finally {
            if (seq.current[key] === n) setLoading((s) => ({ ...s, [key]: false }));
        }
    }, [t]);

    const loadMonth = useCallback(() => Promise.all([
        fetchOne("cards", "/data-audit/cards", { month }, setCards),
        fetchOne("checks", "/data-audit/checks", { month }, setChecks),
    ]), [fetchOne, month]);
    const loadAnoms = useCallback(() => fetchOne("anoms", "/data-audit/anomalies", null, setAnoms), [fetchOne]);
    const loadCoverage = useCallback(
        () => fetchOne("coverage", "/data-audit/coverage", { months: 14 }, setCoverage), [fetchOne]);

    useEffect(() => { loadMonth(); }, [loadMonth]);      // the month picker reloads cards + checks only
    useEffect(() => { loadAnoms(); }, [loadAnoms]);
    const coverageAsked = useRef(false);
    useEffect(() => {
        if (tab === "coverage" && !coverageAsked.current) {
            coverageAsked.current = true;
            loadCoverage();
        }
    }, [tab, loadCoverage]);

    const load = useCallback(() => Promise.all([
        loadMonth(), loadAnoms(), coverageAsked.current ? loadCoverage() : null,
    ]), [loadMonth, loadAnoms, loadCoverage]);
    const busy = Object.values(loading).some(Boolean);

    // «تنظيف» never deletes on the first tap: a dry run returns the exact count and
    // 5 sample rows for the confirm dialog; the real run quarantines every row first.
    const fix = async (key) => {
        setFixing(key);
        try {
            const r = await api.post("/data-audit/anomalies/fix", null, { params: { key, dry_run: true } });
            setPreview(r.data);
        } catch (e) {
            toast.error(apiErrorText(e, t("da_fix_failed")));
        } finally {
            setFixing(null);
        }
    };

    const applyFix = async () => {
        const p = preview;
        if (!p) return;
        setPreview(null);
        setFixing(p.key);
        try {
            const r = await api.post("/data-audit/anomalies/fix", null, { params: { key: p.key, expected: p.count } });
            toast.success(`${t("da_cleaned")} — ${fmtInt(r.data.deleted, lang)}`, {
                description: `${t("da_fix_quarantined")} · ${r.data.run_id}`,
            });
            await load();
        } catch (e) {
            toast.error(apiErrorText(e, t("da_fix_failed")));
        } finally {
            setFixing(null);
        }
    };

    const byPage = useMemo(() => {
        const g = new Map();
        for (const c of cards?.cards || []) {
            if (!g.has(c.page)) g.set(c.page, []);
            g.get(c.page).push(c);
        }
        return [...g.entries()];
    }, [cards]);

    const triggerClass =
        "gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[color:var(--ink-muted)] transition-colors hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white sm:text-sm";

    return (
        <div data-testid={DA.page} className="fin-scope space-y-5">
            <PageHeader
                icon={ShieldCheck}
                title={t("da_title")}
                subtitle={t("da_subtitle")}
                actions={
                    <div className="flex items-center gap-2">
                        <Select value={month} onValueChange={setMonth}>
                            <SelectTrigger data-testid={DA.monthSelect} className="h-9 w-[150px] text-sm">
                                <CalendarRange className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-[320px]">
                                {months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button data-testid={DA.refresh} variant="outline" className="h-9 gap-2" onClick={load} disabled={busy}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                    </div>
                }
            />

            {!cards && !checks ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[86px] w-full rounded-2xl" />)}
                </div>
            ) : (
                <div data-testid={DA.summary} className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <Stat k="with-data" label={t("da_cards_with_data")} value={cards?.summary?.with_data} tone="var(--success)" />
                    <Stat k="partial" label={t("da_cards_partial")} value={cards?.summary?.partial} tone="#B45309" />
                    <Stat k="empty" label={t("da_cards_empty")} value={cards?.summary?.empty}
                        tone={cards?.summary?.empty ? "var(--danger)" : "var(--success)"} />
                    <Stat k="checks-pass" label={t("da_checks_passed")} value={checks?.summary?.passed} tone="var(--success)" />
                    <Stat k="checks-fail" label={t("da_checks_failed")} value={checks?.summary?.failed}
                        tone={checks?.summary?.failed ? "var(--danger)" : "var(--success)"} />
                </div>
            )}

            {checks?.frontier?.running ? (
                <div data-testid={DA.frontier} className="flex items-center gap-2 rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-2.5 text-xs text-[color:var(--ink-muted)]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--brand-orange)]" />
                    {t("da_backfill_running")} <bdi dir="ltr" className="tnum font-semibold">{checks.frontier.current_date}</bdi>
                </div>
            ) : null}

            <Tabs value={tab} onValueChange={setTab} dir={dir} className="space-y-4">
                <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                    <TabsTrigger data-testid={DA.tab("cards")} value="cards" className={triggerClass}>
                        <LayoutGrid className="h-4 w-4" />{t("da_tab_cards")}
                    </TabsTrigger>
                    <TabsTrigger data-testid={DA.tab("checks")} value="checks" className={triggerClass}>
                        <Scale className="h-4 w-4" />{t("da_tab_checks")}
                    </TabsTrigger>
                    <TabsTrigger data-testid={DA.tab("coverage")} value="coverage" className={triggerClass}>
                        <CalendarRange className="h-4 w-4" />{t("da_tab_coverage")}
                    </TabsTrigger>
                    <TabsTrigger data-testid={DA.tab("anomalies")} value="anomalies" className={triggerClass}>
                        <Wrench className="h-4 w-4" />{t("da_tab_anomalies")}
                        {anoms?.anomalies?.length ? (
                            <Badge variant="warning" className="ms-1 text-[10px]">{anoms.anomalies.length}</Badge>
                        ) : null}
                    </TabsTrigger>
                </TabsList>

                {/* ---------------- cards ---------------- */}
                <TabsContent value="cards" className="mt-0 space-y-3 focus-visible:outline-none">
                    {!cards ? <TabSkeleton testId={DA.tabLoading("cards")} /> : null}
                    {byPage.map(([page, rows]) => (
                        <Card key={page} className="rounded-2xl border-[color:var(--border-soft)]">
                            <CardContent className="p-4">
                                <h3 className="mb-2.5 text-sm font-bold text-[color:var(--ink)]">{t(`da_page_${page}`)}</h3>
                                <div className="space-y-1.5">
                                    {rows.map((c) => (
                                        <div key={c.card} data-testid={DA.cardRow(c.page, c.card)}
                                            className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[color:var(--border-soft)] surface-2 px-3 py-2">
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-[color:var(--ink)]">{label(c)}</p>
                                                {c.inputs.map((i, ix) => (
                                                    <p key={ix} className="text-[11px] text-[color:var(--ink-muted)]">
                                                        <bdi dir="ltr">{i.dataset}</bdi>
                                                        {i.status !== "complete" ? ` · ${i.status}` : ""}
                                                        {i.reason ? ` · ${reasonText(i.reason)}` : ""}
                                                    </p>
                                                ))}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Pill status={c.status}>{t(`da_st_${c.status}`)}</Pill>
                                                {c.status !== "complete" ? (
                                                    <Link to={c.inputs.find((i) => i.reason?.fix)?.reason?.fix || c.route}>
                                                        <Button variant="outline" size="sm" className="h-7 gap-1 text-[11px]"
                                                            data-testid={DA.cardFix(c.page, c.card)}>
                                                            <ExternalLink className="h-3 w-3" />{t("da_open")}
                                                        </Button>
                                                    </Link>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </TabsContent>

                {/* ---------------- checks ---------------- */}
                <TabsContent value="checks" className="mt-0 space-y-2 focus-visible:outline-none">
                    <p className="text-xs text-[color:var(--ink-muted)]">{t("da_checks_hint")}</p>
                    {!checks ? <TabSkeleton testId={DA.tabLoading("checks")} /> : null}
                    {[...(checks?.checks || [])]
                        .sort((a, b) => ({ fail: 0, pass: 1, skipped: 2 }[a.status] ?? 3) - ({ fail: 0, pass: 1, skipped: 2 }[b.status] ?? 3))
                        .map((c) => (
                        <div key={c.key} data-testid={DA.checkRow(c.key)}
                            className="rounded-2xl border border-[color:var(--border-soft)] surface px-4 py-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-[color:var(--ink)]">{label(c)}</p>
                                <Pill status={c.status}>{t(`da_st_${c.status}`)}</Pill>
                            </div>
                            <div className="mt-1.5 grid grid-cols-1 gap-1 text-[11px] text-[color:var(--ink-muted)] sm:grid-cols-3">
                                <span><bdi dir="ltr">{c.a_label}</bdi>: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtNumber(c.a, lang, 2)}</bdi></b></span>
                                <span><bdi dir="ltr">{c.b_label}</bdi>: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtNumber(c.b, lang, 2)}</bdi></b></span>
                                {c.status === "skipped" ? (
                                    <span>{lang === "ar" ? c.skip_ar : c.skip_en}</span>
                                ) : (
                                    <span>Δ <b className="tnum" style={{ color: c.status === "pass" ? "var(--success)" : "var(--danger)" }}>
                                        <bdi dir="ltr">{fmtNumber(c.delta, lang, 2)}</bdi></b></span>
                                )}
                            </div>
                        </div>
                        ))}
                </TabsContent>

                {/* ---------------- coverage ---------------- */}
                <TabsContent value="coverage" className="mt-0 focus-visible:outline-none">
                    {!coverage ? <TabSkeleton testId={DA.tabLoading("coverage")} note={t("da_coverage_loading")} /> : (
                    <Card className="rounded-2xl border-[color:var(--border-soft)]">
                        <CardContent className="p-4">
                            <div data-testid={DA.coverage} className="overflow-x-auto" dir="ltr">
                                <table className="w-full min-w-[860px] border-separate" style={{ borderSpacing: "2px" }}>
                                    <thead>
                                        <tr>
                                            <th className="sticky start-0 z-10 bg-[color:var(--bg-surface)] px-2 text-start text-[11px] font-semibold text-[color:var(--ink-muted)]">
                                                {t("da_dataset")}
                                            </th>
                                            {(coverage?.months || []).map((m) => (
                                                <th key={m} className="text-center text-[10px] font-medium text-[color:var(--ink-muted)] tnum">
                                                    {m.slice(2)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(coverage?.datasets || []).map((ds) => (
                                            <tr key={ds.key}>
                                                <td className="sticky start-0 z-10 max-w-[220px] truncate bg-[color:var(--bg-surface)] px-2 text-[11px] font-medium text-[color:var(--ink)]">
                                                    {label({ label_ar: ds.label_ar, label_en: ds.label_en })}
                                                </td>
                                                {(coverage?.months || []).map((m) => {
                                                    const cell = ds.cells[m] || {};
                                                    const tone = STATUS_TONE[cell.status] || STATUS_TONE.skipped;
                                                    return (
                                                        <td key={m} data-testid={DA.covCell(ds.key, m)}
                                                            title={`${cell.status}${cell.detail ? " · " + cell.detail : ""}${cell.reason ? " · " + reasonText(cell.reason) : ""}`}
                                                            className="h-7 rounded-[4px] text-center align-middle text-[9px] font-semibold"
                                                            style={{ backgroundColor: tone.bg, color: tone.fg }}>
                                                            {cell.status === "complete" ? "✓" : cell.status === "partial" ? "~" : "·"}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                    )}
                </TabsContent>

                {/* ---------------- anomalies ---------------- */}
                <TabsContent value="anomalies" className="mt-0 space-y-2 focus-visible:outline-none">
                    {!anoms ? <TabSkeleton testId={DA.tabLoading("anomalies")} /> : !anoms.anomalies?.length ? (
                        <div data-testid={DA.noAnomalies} className="rounded-2xl border border-[color:var(--border-soft)] surface px-4 py-8 text-center">
                            <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-[color:var(--success)]" />
                            <p className="text-sm text-[color:var(--ink-muted)]">{t("da_no_anomalies")}</p>
                        </div>
                    ) : anoms.anomalies.map((a) => (
                        <div key={a.key} data-testid={DA.anomalyRow(a.key)}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[color:var(--border-soft)] surface px-4 py-3">
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-[color:var(--ink)]">{lang === "ar" ? a.ar : a.en}</p>
                                <p className="text-[11px] text-[color:var(--ink-muted)]">
                                    <bdi dir="ltr">{a.key}</bdi> · {fmtInt(a.count || 0, lang)}
                                </p>
                            </div>
                            {a.fixable ? (
                                <Button data-testid={DA.anomalyFix(a.key)} variant="outline" size="sm"
                                    className="h-8 gap-1.5 text-[11px]" onClick={() => fix(a.key)} disabled={fixing === a.key}>
                                    {fixing === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                    {t("da_clean_up")}
                                </Button>
                            ) : <Pill status="partial">{t("da_review")}</Pill>}
                        </div>
                    ))}
                </TabsContent>
            </Tabs>

            <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
                <AlertDialogContent data-testid={DA.fixDialog} className="max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("da_fix_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm text-[color:var(--ink-muted)]">
                                <p data-testid={DA.fixCount}>
                                    {t("da_fix_confirm_body")
                                        .replace("{n}", fmtInt(preview?.count || 0, lang))
                                        .replace("{coll}", preview?.collection || "")}
                                </p>
                                <p>{t("da_fix_quarantine_note")}</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {preview?.sample?.length ? (
                        // min-w-0: a grid item otherwise grows to its widest row, and in RTL
                        // that pushed the text and both buttons off-screen
                        <div data-testid={DA.fixSample} className="min-w-0 space-y-1">
                            <p className="text-xs font-semibold text-[color:var(--ink)]">{t("da_fix_sample")}</p>
                            <div dir="ltr" className="max-h-48 overflow-auto rounded-lg border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] p-2">
                                {preview.sample.map((row, i) => (
                                    <code key={i} className="block whitespace-pre-wrap break-all font-mono text-[10px] text-[color:var(--ink-muted)]">
                                        {JSON.stringify(row)}
                                    </code>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={DA.fixConfirm} onClick={applyFix} disabled={!preview?.count}
                            className="bg-[color:var(--danger)] hover:bg-[#9A1C14] text-white">
                            {t("da_fix_confirm_action").replace("{n}", fmtInt(preview?.count || 0, lang))}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
